import {Component, inject, Input, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {NgbActiveModal, NgbModal, NgbTooltip} from '@ng-bootstrap/ng-bootstrap';
import {AlertService} from "@core/services/alert.service";
import {CompaniesApiService} from "@core/services/api/companies-api.service";
import {AppTableComponent} from '@shared/components/tables/app-table/app-table.component';
import {AppTableBodyTemplateDirective, AppTableHeaderTemplateDirective} from "@shared/components/tables/app-table/directives/app-table.directives";
import {CompanyDebtDetailsDialogComponent} from "@features/companies/components/company-debt-details-dialog/company-debt-details-dialog.component";
import {copyToClipboard} from "@shared/commons/keyboard.commons";
import {CpfCnpjPipe} from "@shared/pipes/app-cpfcnpj.pipe";
import {AppPhoneComponent} from "@shared/components/commons/app-phone/app-phone.component";
import {CompanyDetailsComponent} from "@features/companies/components/company-details/company-details.component";
import {AppLoadingOverlayComponent} from "@shared/components/commons/app-loading-overlay/app-loading-overlay.component";
import {PersonTypeHelper} from "@core/models/person-type.enums";
import {NegotiationTypeHelper} from "@core/models/negotiation-type.helper";

@Component({
    selector: 'app-company-debts',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        AppTableComponent,
        AppTableHeaderTemplateDirective,
        AppTableBodyTemplateDirective,
        CpfCnpjPipe,
        AppPhoneComponent,
        AppLoadingOverlayComponent,
        NgbTooltip
    ],
    templateUrl: './company-debts.component.html',
    styleUrl: './company-debts.component.scss'
})
export class CompanyDebtsComponent implements OnInit {

    @Input() companyId!: number;

    protected company: any = null;
    protected debts: any[] = [];
    protected isLoading = false;
    protected PersonTypeHelper = PersonTypeHelper;
    protected NegotiationTypeHelper = NegotiationTypeHelper;

    private alertService = inject(AlertService);
    private companiesApiService = inject(CompaniesApiService);
    protected activeModal = inject(NgbActiveModal);
    private modalService = inject(NgbModal);

    ngOnInit(): void {
        if (this.companyId) {
            this.findById(this.companyId);
            this.findAll(this.companyId);
        }
    }

    private findById(companyId: number): void {
        this.isLoading = true;
        this.companiesApiService.findById(companyId)
            .subscribe({
                next: (response) => {
                    this.company = response.data;
                    this.isLoading = false;
                },
                error: () => {
                    this.alertService.operationFailure();
                    this.isLoading = false;
                }
            })
    }

    findAll(companyId: number): void {
        this.isLoading = true;
        this.companiesApiService.findAllDebts(companyId)
            .subscribe({
                next: (response) => {
                    this.debts = response.data || [];
                    this.isLoading = false;
                },
                error: () => {
                    this.alertService.operationFailure();
                    this.isLoading = false;
                }
            })
    }


    getFullAddress(company: any): string {
        const parts: string[] = [];

        if (company.addressType && company.streetName) {
            parts.push(`${company.addressType} ${company.streetName}`);
        } else if (company.streetName) {
            parts.push(company.streetName);
        }

        if (company.number) {
            parts.push(company.number);
        }

        if (company.complement) {
            parts.push(company.complement);
        }

        if (company.neighborhood) {
            parts.push(company.neighborhood);
        }

        if (company.city && company.state) {
            parts.push(`${company.city}/${company.state}`);
        }

        if (company.zipcode) {
            parts.push(`CEP: ${company.zipcode}`);
        }

        return parts.join(', ');
    }

    getFullPhone(company: any): string {
        if (company.areaCode && company.phone) {
            return `(${company.areaCode}) ${company.phone}`;
        }
        return company.phone || '';
    }

    getStatusBadgeClass(status: string): string {
        const classes = {
            'DEFERIDO E CONSOLIDADO': 'badge bg-success-subtle text-success-emphasis',
            'EM NEGOCIACAO': 'badge bg-warning-subtle text-warning-emphasis',
            'AGUARDANDO DEFERIMENTO': 'badge bg-info-subtle text-info-emphasis',
            'CANCELADO': 'badge bg-danger-subtle text-danger-emphasis',
            'RESCINDIDO': 'badge bg-danger-subtle text-danger-emphasis'
        };
        return classes[status as keyof typeof classes] || 'badge bg-secondary-subtle text-secondary-emphasis';
    }

    getStatusLabel(status?: string): string {
        if (!status) return '—';
        return status;
    }

    close(): void {
        this.activeModal.close();
    }

    openDetailsModal(debt: any): void {
        const modalRef = this.modalService.open(CompanyDebtDetailsDialogComponent, {
            centered: true,
            size: 'lg',
            scrollable: true
        });
        modalRef.componentInstance.debt = debt;

        modalRef.result.then(
            (result) => {
            },
            () => {
                // Modal fechado sem ação
            }
        );
    }

    protected enrichByDocumentNumber(documentNumber: string): void {
        if (!documentNumber) return;

        this.companiesApiService.enrich(documentNumber)
            .subscribe({
                next: () => {
                    this.alertService.successfulOperation();
                    this.findById(this.company.id);
                },
                error: (_error) => {
                    if (_error.error.code === 'CNPJWS_RATE_LIMIT') {
                        this.alertService.error("Ops", `Limite de consultas atingido, aguarde 5 minutos para tentar novamente.`);
                        return;
                    }

                    this.alertService.operationFailure()
                }
            })
    }

    protected async copy(value: any) {
        await copyToClipboard(value);
        this.alertService.success('messages.success.label', 'messages.success.copy_to_clipboard');
    }

    protected viewCompanyDetails(companyId: number): void {
        if (!companyId) return;

        const modalRef = this.modalService.open(CompanyDetailsComponent, {
            size: 'xl',
            centered: true,
            scrollable: true
        });

        modalRef.componentInstance.companyId = companyId;
    }

}

